import {
  IClientInvoice,
  IClientInvoiceUpdateRequest,
  IInvoice,
  InvoiceStatus,
  Intervals,
  IPayRequest,
  IPayrollSettings,
  IPayrollSettingsUpdate
} from "@wingspanhq/payments/dist/interfaces";
import {
  WSMutationConfig,
  WSMutationsConfig,
  WSQueryCache
} from "@ws-react-query";
import { resolveErrorMessage } from "@utils/resolveErrorMessage";
import { isEqual } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { InvoicesFormValues } from "../../Invoices/components/InvoicesForm/InvoicesForm";
import { AttachmentValues } from "../../Invoices/components/InvoicesForm/OtherSection";
import {
  PayableFormValues,
  SendPayableInvoiceAction
} from "../../Invoices/components/PayableForm/PayableForm";
import { selectorBillingAttachmentSync } from "../../Invoices/selectors/selectorBillingAttachmentSync";
import { selectorLineItemsCreate } from "../../Invoices/selectors/selectorLineItemsCreate";
import { selectorRecurrenceSchedule } from "../../Invoices/selectors/selectorRecurrenceSchedule";
import { selectorRecurringLineItems } from "../../Invoices/selectors/selectorRecurringLineItems";
import { useDownloadPdf } from "../../Invoices/utils";
import {
  attachmentsFormDataToCreateRequest,
  generateDefaultDueDateSetByPayer,
  generateDueDate,
  generateSendDate,
  useInvoiceFormGoBack
} from "../../Invoices/utils/invoicesFormUtils";
import { paymentsService } from "../../services/payments";
import {
  calculateCalendarDayDifference,
  convertDateToMidday,
  isToday
} from "../../utils/dates";
import { useWSMutation } from "../helpers";
import { useUserId } from "../hooks/helpers";
import {
  QUERY_CLIENT_INVOICE,
  QUERY_INVOICE,
  QUERY_INVOICES,
  QUERY_PAYABLES,
  QUERY_PAYROLL_SETTINGS
} from "./keys";

import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import {
  CreditFeeConfig,
  InvoiceSplitCreate,
  InvoiceStatus as InvoiceStatusV3,
  LateFeeConfig,
  LateFeeConfigFrequencyInterval,
  Invoice,
  Payable,
  PaymentDocumentCurrency,
  UpdatePayableWorkflowStatusRequestPayerApprovalStatus,
  PaymentMethod as PaymentMethodV3,
  RecurrenceScheduleFrequency,
  RecurringDocumentStatus,
  RecurringInvoice,
  RecurringInvoiceSplit,
  RecurringInvoiceSplitSplitType
} from "@wingspanhq/wingspan-api-sdk";
import {
  invoicesApi,
  payablesApi,
  payersApi,
  recurringInvoicesApi
} from "@api";
import { IdempotencyKey } from "../../v3/utils/idempotencyKey";
import {
  queriesInvoices,
  queriesInvoiceRows,
  queriesPayableRows,
  queriesPayables,
  queriesRecurringInvoices
} from "@query";
import { useDefaultCurrency } from "../../shared/hooks/useDefaultCurrency";
import { WSServiceError } from "../../utils/serviceHelper";
import { QUERY_PAYER_ROWS } from "../search/payer/queries/useQueryPayerRows";

/**
 * Brings an invoice's v3 attachments in line with what the form submitted
 * (WIN-15134). v3 has no bulk attachment write, so the reconciliation is a set
 * of per-file bind/detach calls against what is currently bound.
 *
 * A failure propagates and aborts the caller's mutation. An invoice missing the
 * files the user attached is not finished, so the save fails and the invoice
 * stays a `Created` draft rather than being sent to the client without them —
 * a recoverable state the user can retry, unlike an incomplete invoice the
 * client has already received.
 *
 * That means the create must be replayable: `createInvoice` carries a stable
 * `IdempotencyKey` so retrying the failed save re-uses the invoice already
 * created instead of adding a second one (PLA-3531).
 *
 * The form seeds `memberFileId` from the v3 vault upload (OtherSection); the
 * bind endpoint accepts a VaultFile id there since WIN-15702 shipped.
 */

const syncInvoiceAttachments = async (
  invoiceId: string,
  // Both billing forms can submit with the attachments field unset, so this is
  // genuinely optional — the guard below is load-bearing (PLA-3531).
  formAttachments: AttachmentValues[] | undefined
) => {
  // An empty/unset field means nothing was seeded and nothing added, so
  // there is nothing to reconcile — skip the read rather than probing on
  // every save.
  if (!formAttachments || formAttachments.length === 0) {
    return;
  }

  const desiredMemberFileIds =
    attachmentsFormDataToCreateRequest(formAttachments);
  const current = await invoicesApi.listInvoiceAttachments({ invoiceId });

  const { attachMemberFileIds, detachAttachmentIds } =
    selectorBillingAttachmentSync({
      current: current.data ?? [],
      desiredMemberFileIds
    });

  for (const attachmentId of detachAttachmentIds) {
    await invoicesApi.detachInvoiceDocument({ invoiceId, attachmentId });
  }

  for (const memberFileId of attachMemberFileIds) {
    await invoicesApi.attachInvoiceDocument({
      invoiceId,
      billingAttachmentCreate: { memberFileId }
    });
  }
};

/** The payable twin of `syncInvoiceAttachments` above — same reconciliation and
 * the same fail-the-save contract, against `payablesApi`'s bind verbs instead
 * of `invoicesApi`'s. */
const syncPayableAttachments = async (
  payableId: string,
  formAttachments: AttachmentValues[] | undefined
) => {
  // The payable form submits `other.attachments` unset when the payer added no
  // files, so this guard has to cover undefined and not just an empty list.
  // Reading `.length` off undefined here threw a raw TypeError on every
  // no-attachment save, which aborted the payable's "open" step and left it
  // stuck in Draft (PLA-3531). With the guard, that path no longer fails at
  // all; a genuine bind failure still does, by design.
  if (!formAttachments || formAttachments.length === 0) {
    return;
  }

  const desiredMemberFileIds =
    attachmentsFormDataToCreateRequest(formAttachments);
  const current = await payablesApi.listPayableAttachments({ payableId });

  const { attachMemberFileIds, detachAttachmentIds } =
    selectorBillingAttachmentSync({
      current: current.data ?? [],
      desiredMemberFileIds
    });

  for (const attachmentId of detachAttachmentIds) {
    await payablesApi.detachPayableDocument({ payableId, attachmentId });
  }

  for (const memberFileId of attachMemberFileIds) {
    await payablesApi.attachPayableDocument({
      payableId,
      billingAttachmentCreate: { memberFileId }
    });
  }
};

export const useMutationAcceptInvoice = () => {
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      await invoicesApi.acceptInvoice({ invoiceId });
    },
    {
      onSuccess: (_, { invoiceId }) => {
        openSnackbar({
          type: "success",
          message: "Invoice accepted"
        });

        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
        WSQueryCache.invalidateQueries(
          queriesInvoices.getInvoice.getQueryKey({ invoiceId })
        );
      },
      onError: error => {
        openSnackbar({
          type: "warning",
          message: resolveErrorMessage({
            error,
            context: "Other"
          })
        });
      }
    }
  );
};

export const useMutationSaveInvoice = () => {
  const userId = useUserId();
  const goBack = useInvoiceFormGoBack();
  const history = useHistory();
  const location = useLocation();

  return useWSMutation<
    {
      savedInvoice?: Invoice;
      savedInvoiceId?: string;
      savedRecurringInvoice?: RecurringInvoice;
    },
    WSServiceError,
    {
      invoiceId?: string;
      invoiceTemplateId?: string;
      data: InvoicesFormValues & {
        dueInDays?: number;
      };
      send?: boolean;
      resubmit?: boolean;
      currency: Currency;
    }
  >(
    async ({
      invoiceId,
      invoiceTemplateId,
      data,
      send,
      resubmit,
      currency
    }) => {
      let invoice: IInvoice | undefined;
      let recurringInvoice: RecurringInvoice | undefined;
      let savedInvoice: Invoice | undefined;
      let savedInvoiceId: string | undefined;
      let savedRecurringInvoice: RecurringInvoice | undefined;
      // Set only on the create path, where a retry of a failed save must not
      // add a second invoice. Released once the whole save has landed.
      let createIdempotencyKey: IdempotencyKey | undefined;

      if (invoiceTemplateId) {
        recurringInvoice = await recurringInvoicesApi.getRecurringInvoice({
          recurringInvoiceId: invoiceTemplateId
        });
      }

      // Payer
      const payerId = data.payer?.payerId as string;
      const payerEngagementId = data.payer?.payerEngagementId as string;

      // The picker writes v3 ids, so the CC sync resolves end-to-end
      // (`Payer.notificationCcEmails` / `PayerUpdate.notificationCcEmails`,
      // WIN-15229). It stays wrapped in a catch: a CC sync failure should not
      // fail the invoice save.
      try {
        const payer = await payersApi.getPayer({ payerId });

        const additionalEmails = (data.payer?.emailsCC || [])
          .map(i => i.email)
          .filter(e => !!e);
        if (!isEqual(additionalEmails, payer.notificationCcEmails || [])) {
          // `[]` explicitly clears the stored list (WIN-15229 semantics); omitting the
          // field would preserve it.
          await payersApi.updatePayer({
            payerId,
            payerUpdate: { notificationCcEmails: additionalEmails }
          });
        }
      } catch (error) {
        console.error("Failed to update client email CCs", error);
      }

      // Only the create and invoice-template paths put the engagement in a
      // payload (`InvoiceCreate.payerEngagementId` / the template's
      // `memberClientId`). `updateInvoice` has no field for it, so an edit must
      // not be gated on one — v1-created invoices reach v3 without a
      // `payerEngagementId` and would otherwise be permanently unsavable.
      if (!invoiceId && !payerEngagementId) {
        throw new Error("No payerEngagementId set");
      }

      const sendDate = generateSendDate({
        send: data.send
      });

      let dueDate: Date;
      if (data.dueInDays !== undefined) {
        // if dueInDays is set by payer then generate due date based on it
        dueDate = generateDefaultDueDateSetByPayer({
          dueInDays: data.dueInDays,
          sendDate
        });
      } else {
        // Generate due date based on form values and send date
        dueDate = generateDueDate({
          formValues: {
            due: data.due,
            customDueDate: data.customDueDate
          },
          sendDate
        });
      }

      // v3 field construction, shared by the create and edit paths below.
      const acceptedPaymentMethodsV3: PaymentMethodV3[] = data.paymentMethods
        .acceptAllPMs
        ? [PaymentMethodV3.CREDIT, PaymentMethodV3.ACH, PaymentMethodV3.MANUAL]
        : [
            ...(data.paymentMethods.acceptCreditCard
              ? [PaymentMethodV3.CREDIT]
              : []),
            ...(data.paymentMethods.acceptACH ? [PaymentMethodV3.ACH] : []),
            ...(data.paymentMethods.acceptManual
              ? [PaymentMethodV3.MANUAL]
              : [])
          ];

      const creditFeeHandlingV3: CreditFeeConfig = data.advanced
        .creditFeeHandling?.clientPaysProcessingFees
        ? {
            payerFlatFeePercentage:
              data.advanced.creditFeeHandling.clientProcessingFeePercentage
          }
        : // The API requires the two percentage fields together — sending
          // `payeeFeePercentage` without `payerFeePercentage` is a 422.
          { payeeFeePercentage: 100, payerFeePercentage: 0 };

      let lateFeeHandlingV3: LateFeeConfig | undefined;
      if (data.includesLateFee) {
        lateFeeHandlingV3 = data.lateFee.overriden
          ? {
              lateFeeAmount: data.lateFee.compoundedAmount,
              frequency: {
                interval:
                  LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_WEEKLY,
                every: 1
              }
            }
          : {
              ...(data.lateFee.type === "percent"
                ? { lateFeePercentage: data.lateFee.amount }
                : {}),
              ...(data.lateFee.type === "fixed"
                ? { lateFeeAmount: data.lateFee.amount }
                : {}),
              frequency: {
                interval:
                  data.lateFee.interval === Intervals.Month
                    ? LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_MONTHLY
                    : LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_WEEKLY,
                every: data.lateFee.every
              }
            };
      }

      const notificationPreferencesV3 = {
        shouldSendInvoice: data.advanced.sendEmails,
        shouldSendReceipt: data.advanced.sendEmails,
        shouldSendReminders:
          data.advanced.sendEmails && !!data.advanced.sendReminders
      };

      const metadataV3 = {
        subject: data.subject,
        ...(data.other.projectName
          ? { projectName: data.other.projectName }
          : {}),
        invoiceDate: convertDateToMidday(data.advanced.date).toISOString(),
        ...(data.purchaseOrderNumber
          ? { purchaseOrderNumber: data.purchaseOrderNumber }
          : {})
      };

      const schedule = selectorRecurrenceSchedule({
        recurring: data.recurring,
        frequency: data.frequency,
        sendDate
      });

      const paymentTermsDays =
        data.dueInDays ?? calculateCalendarDayDifference(dueDate, sendDate);

      const recurringSplits: RecurringInvoiceSplit[] = data.splits.flatMap(
        split => {
          if (
            split.remove ||
            !split.payee?.payeeId ||
            !split.payee?.payeeEngagementId ||
            !split.amount
          ) {
            return [];
          }

          return [
            {
              payeeId: split.payee.payeeId,
              payeeEngagementId: split.payee.payeeEngagementId,
              splitType: RecurringInvoiceSplitSplitType.PAYEE_EARNINGS,
              amount: split.amount,
              description: split.description
            }
          ];
        }
      );

      const storedFrequency = recurringInvoice?.schedule?.frequency;
      const isScheduleEditable =
        !storedFrequency ||
        storedFrequency === RecurrenceScheduleFrequency.WEEKLY ||
        storedFrequency === RecurrenceScheduleFrequency.MONTHLY;

      const recurringMetadata = {
        ...(recurringInvoice?.metadata?.parentInvoiceTemplateId
          ? {
              parentInvoiceTemplateId:
                recurringInvoice.metadata.parentInvoiceTemplateId
            }
          : {}),
        ...metadataV3
      };

      const invoiceTemplateBody = {
        paymentTermsDays,
        lineItems: selectorRecurringLineItems(data.lineItems),
        splits: recurringSplits,
        acceptedPaymentMethods: acceptedPaymentMethodsV3,
        creditFeeHandling: creditFeeHandlingV3,
        lateFeeHandling: lateFeeHandlingV3,
        notificationPreferences: notificationPreferencesV3,
        customAttachmentIds: attachmentsFormDataToCreateRequest(
          data.other.attachments
        ),
        notes: data.other.notes
      };

      if (
        !data.recurring &&
        (data.send.type === "immediately" || isToday(sendDate))
      ) {
        // If invoice is not repeats and have to be sent immediately – we can work just with invoice entity

        if (invoiceId) {
          // v3 `InvoiceUpdate` carries no client/engagement, currency, splits or
          // member workflow status: re-pointing an invoice at a different client,
          // editing its contractor splits, and the dispute-resubmit flip are all
          // outside the update contract. The invoice's own fields below are what
          // an edit can change.
          savedInvoice = await invoicesApi.updateInvoice({
            invoiceId,
            invoiceUpdate: {
              lineItems: selectorLineItemsCreate(data.lineItems),
              dueDate,
              notes: data.other.notes,
              acceptedPaymentMethods: acceptedPaymentMethodsV3,
              creditFeeHandling: creditFeeHandlingV3,
              lateFeeHandling: lateFeeHandlingV3,
              notificationPreferences: notificationPreferencesV3,
              metadata: metadataV3
            }
          });
          savedInvoiceId = savedInvoice.id;

          // Attachments no longer ride along on the update body — they are
          // reconciled through the v3 per-file bind/detach verbs (WIN-15134).
          // A bind failure fails the save: the invoice keeps its previous
          // attachments and the user is told, rather than the edit appearing to
          // have applied. Retrying an update is naturally idempotent.
          await syncInvoiceAttachments(invoiceId, data.other.attachments);
        } else if (invoiceTemplateId) {
          savedRecurringInvoice =
            await recurringInvoicesApi.updateRecurringInvoice({
              recurringInvoiceId: invoiceTemplateId,
              recurringInvoiceUpdate: {
                ...(isScheduleEditable ? { schedule } : {}),
                invoiceTemplate: invoiceTemplateBody,
                metadata: recurringMetadata
              }
            });
        } else {
          const splits: InvoiceSplitCreate[] = data.splits.flatMap(split => {
            if (
              split.remove ||
              !split.payee?.payeeId ||
              !split.payee?.payeeEngagementId ||
              !split.amount
            ) {
              return [];
            }

            return [
              {
                payeeId: split.payee.payeeId,
                payeeEngagementId: split.payee.payeeEngagementId,
                amount: split.amount,
                currency,
                description: split.description
              }
            ];
          });

          // Stable across retries of the same logical invoice, so replaying a
          // save that failed *after* the invoice was created returns that same
          // invoice instead of creating another (PLA-3531). The line items are
          // serialized in full rather than reduced to a total: two different
          // invoices that happen to share a total must not dedup together.
          createIdempotencyKey = new IdempotencyKey({
            scope: "createInvoice",
            payerEngagementId: payerEngagementId ?? "",
            currency: String(currency),
            dueDate: dueDate?.toISOString() ?? "",
            subject: data.subject ?? "",
            lineItems: JSON.stringify(selectorLineItemsCreate(data.lineItems))
          });

          const createdInvoice = await invoicesApi.createInvoice({
            idempotencyKey: createIdempotencyKey.value,
            invoiceCreate: {
              payerEngagementId,
              currency: currency as unknown as PaymentDocumentCurrency,
              dueDate,
              lineItems: selectorLineItemsCreate(data.lineItems),
              splits,
              notes: data.other.notes,
              acceptedPaymentMethods: acceptedPaymentMethodsV3,
              creditFeeHandling: creditFeeHandlingV3,
              lateFeeHandling: lateFeeHandlingV3,
              notificationPreferences: notificationPreferencesV3,
              metadata: metadataV3
            }
          });
          savedInvoiceId = createdInvoice.id;

          // v3 `InvoiceCreate` carries no attachments, so the files the form
          // uploaded are bound afterwards, and deliberately *before* the
          // optional send below: if binding fails the save fails here, the
          // invoice stays a draft, and nothing is sent to the client without
          // its attachments. The idempotency key above makes the retry safe.
          if (savedInvoiceId) {
            await syncInvoiceAttachments(
              savedInvoiceId,
              data.other.attachments
            );
          }
        }
      } else {
        if (invoiceTemplateId) {
          savedRecurringInvoice =
            await recurringInvoicesApi.updateRecurringInvoice({
              recurringInvoiceId: invoiceTemplateId,
              recurringInvoiceUpdate: {
                ...(isScheduleEditable ? { schedule } : {}),
                shouldAutoSend: data.advanced.sendEmails,
                invoiceTemplate: invoiceTemplateBody,
                metadata: recurringMetadata
              }
            });
        } else {
          if (invoiceId) {
            // Only this branch (turning an invoice into a recurring/scheduled
            // template) needs the existing invoice, so the read stays local to
            // it — the plain edit path above must not pay for a v1 fetch.
            invoice = await paymentsService.invoice.get(invoiceId);

            if (invoice?.status === InvoiceStatus.Draft) {
              await paymentsService.invoice.delete(invoiceId);
            }
          }

          savedRecurringInvoice =
            await recurringInvoicesApi.createRecurringInvoice({
              recurringInvoiceCreate: {
                payerId,
                payerEngagementId,
                currency,
                schedule,
                shouldAutoSend: data.advanced.sendEmails,
                invoiceTemplate: invoiceTemplateBody,
                metadata: metadataV3,
                status: RecurringDocumentStatus.DRAFT
              }
            });
        }
      }

      if (send) {
        if (savedInvoiceId) {
          await invoicesApi.sendInvoice({ invoiceId: savedInvoiceId });

          history.push(
            `/member/invoices/${savedInvoiceId}/send-success`,
            location.state
          );
        }

        if (savedRecurringInvoice?.id) {
          const recurringInvoiceId = savedRecurringInvoice.id;

          if (savedRecurringInvoice.status === RecurringDocumentStatus.DRAFT) {
            savedRecurringInvoice =
              await recurringInvoicesApi.updateRecurringInvoice({
                recurringInvoiceId,
                recurringInvoiceUpdate: {
                  status: RecurringDocumentStatus.ACTIVE
                }
              });
          }

          const isDueNow =
            !!savedRecurringInvoice.nextOccurrenceDate &&
            (isToday(savedRecurringInvoice.nextOccurrenceDate) ||
              savedRecurringInvoice.nextOccurrenceDate < new Date());

          const createdInvoice = isDueNow
            ? await recurringInvoicesApi.generateRecurringInvoiceNow({
                recurringInvoiceId
              })
            : undefined;

          if (createdInvoice?.id) {
            history.push(
              `/member/invoices/${createdInvoice.id}/send-success`,
              location.state
            );
          } else {
            history.push(
              `/member/invoices/template/${recurringInvoiceId}/send-success`,
              location.state
            );
          }
        }
      }

      // Everything landed — attachments and, if requested, the send. Release
      // the key so a later, intentional repeat of the same invoice is treated
      // as a new one rather than replaying this invoice.
      createIdempotencyKey?.destroy();

      return {
        savedInvoice,
        savedInvoiceId,
        savedRecurringInvoice
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ savedInvoiceId, savedRecurringInvoice }, { send }) => {
        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries(QUERY_PAYER_ROWS);
        WSQueryCache.invalidateQueries(queriesInvoices.getInvoice.queryKeyBase);
        WSQueryCache.invalidateQueries(
          queriesInvoices.listInvoices.queryKeyBase
        );
        WSQueryCache.invalidateQueries(
          queriesInvoices.searchInvoices.queryKeyBase
        );

        if (savedInvoiceId) {
          WSQueryCache.invalidateQueries([QUERY_INVOICE, savedInvoiceId]);
        }
        if (savedRecurringInvoice?.id) {
          WSQueryCache.invalidateQueries(
            queriesRecurringInvoices.getRecurringInvoice.getQueryKey({
              recurringInvoiceId: savedRecurringInvoice.id
            })
          );
          WSQueryCache.invalidateQueries(
            queriesRecurringInvoices.listRecurringInvoices.queryKeyBase
          );
        }

        if (!send) {
          goBack();
        }
      }
    }
  );
};

// v3's `/v3/search/invoice-rows` projection lags the send write briefly, so
// invalidating right after `sendInvoice` resolves can refetch a row that
// still reports its pre-send status. Poll the row itself (mirrors v1's
// poll-until-row-updates wait in `paymentsService.invoice.update`) instead of
// invalidating blind.
const waitForSentInvoiceRow = async (invoiceId: string): Promise<void> => {
  const MAX_ATTEMPTS = 5;
  const DELAY_MS = 300;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const page = await queriesInvoiceRows.searchInvoiceRows.fetchQueryData({
      filter: { id: { eq: invoiceId } },
      page: { size: 1 }
    });

    if (page.data?.[0]?.status !== InvoiceStatusV3.OPENED) {
      await new Promise(resolve => setTimeout(resolve, DELAY_MS));
    } else {
      return;
    }
  }
};

export const useMutationSendInvoice = () => {
  const history = useHistory();
  const location = useLocation();
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    void,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      if (invoiceId) {
        await invoicesApi.sendInvoice({ invoiceId });
        await waitForSentInvoiceRow(invoiceId);

        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
        WSQueryCache.invalidateQueries(
          queriesInvoices.getInvoice.getQueryKey({ invoiceId })
        );

        history.push(
          `/member/invoices/${invoiceId}/send-success`,
          location.state
        );
      }
    },
    {
      throwOnError: false,
      onSuccess: () => {
        openSnackbar({
          type: "success",
          message: "Invoice has been sent"
        });

        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
      },
      onError: error => {
        openSnackbar({
          type: "warning",
          message: resolveErrorMessage({
            error,
            context: "InvoiceForm"
          })
        });
      }
    }
  );
};

export const useMutationUpdateClientInvoice = (invoiceId: string) => {
  return useWSMutation<
    IClientInvoice,
    WSServiceError,
    IClientInvoiceUpdateRequest
  >(data => paymentsService.client.invoice.update(invoiceId, data), {
    throwOnError: false,
    dependencies: [[QUERY_CLIENT_INVOICE, invoiceId]]
  });
};

export const useMutationPayClientInvoice = (
  invoiceId: string,
  config?: WSMutationsConfig<IClientInvoice, WSServiceError, IPayRequest>
) => {
  return useWSMutation<IClientInvoice, WSServiceError, IPayRequest>(
    data => paymentsService.client.invoice.pay(invoiceId, data),
    {
      throwOnError: false,
      dependencies: [[QUERY_CLIENT_INVOICE, invoiceId]],
      ...config
    }
  );
};

export const useMutationSavePayable = () => {
  const defaultCurrency = useDefaultCurrency();

  return useWSMutation<
    {
      savedPayable: Payable;
    },
    WSServiceError,
    {
      payableId?: string;
      data: PayableFormValues;
      resubmit?: boolean;
    }
  >(
    async ({ data, payableId }) => {
      // v3 `PayableUpdate` carries only line items, dates, notes and metadata —
      // the v1 body's currency, credit-fee split and workflow sub-status have no
      // update-side twin, so an edit no longer rewrites them. `resubmit` (the v1
      // dispute-resubmit workflow flip) likewise has no v3 field; the dispute
      // resolution itself runs through the workflow-status verb.
      const metadata = {
        ...(data.other.projectName
          ? { projectName: data.other.projectName }
          : {}),
        ...(data.purchaseOrderNumber
          ? { purchaseOrderNumber: data.purchaseOrderNumber }
          : {})
      };

      const lineItems =
        data.lineItems && data.lineItems.length > 0
          ? selectorLineItemsCreate(data.lineItems)
          : [{ description: "", totalCost: 0 }];

      let savedPayable: Payable;
      // Create path only — see the invoice save above.
      let createIdempotencyKey: IdempotencyKey | undefined;

      if (payableId) {
        savedPayable = await payablesApi.updatePayable({
          payableId,
          ifMatch: "*",
          payableUpdate: {
            lineItems,
            dueDate: data.payDate,
            notes: data.other.notes,
            metadata
          }
        });
      } else {
        if (!data.payee) throw new Error("No payee set");

        // Same replay guarantee as the invoice create: a save that fails while
        // binding attachments must not add a second payable when retried.
        createIdempotencyKey = new IdempotencyKey({
          scope: "createPayable",
          payeeEngagementId: data.payee.payeeEngagementId ?? "",
          currency: String(defaultCurrency),
          dueDate: data.payDate?.toISOString() ?? "",
          lineItems: JSON.stringify(lineItems)
        });

        savedPayable = await payablesApi.createPayable({
          idempotencyKey: createIdempotencyKey.value,
          payableCreate: {
            // `PayableCreate` is an XOR: exactly one of `payeeId` /
            // `payeeEngagementId`. The engagement is the more specific of the
            // two (and implies the payee), and the form always carries it.
            payeeEngagementId: data.payee.payeeEngagementId,
            currency: defaultCurrency as unknown as PaymentDocumentCurrency,
            lineItems,
            dueDate: data.payDate,
            notes: data.other.notes,
            metadata
          }
        });
      }

      // v3 has no bulk attachment write on payables either — reconcile the
      // form's attachments against what's bound, same as invoices. A bind
      // failure fails the save, so the caller's "open" step does not run and
      // the payable stays a Draft rather than being opened without its files.
      if (savedPayable.id) {
        await syncPayableAttachments(savedPayable.id, data.other.attachments);
      }

      createIdempotencyKey?.destroy();

      return {
        savedPayable
      };
    },
    {
      throwOnError: false,
      dependencies: [QUERY_PAYABLES]
    }
  );
};

export const useMutationSendPayable = () => {
  return useWSMutation<
    {
      payable?: Payable;
    },
    WSServiceError,
    {
      action: SendPayableInvoiceAction;
      payableId: string;
    }
  >(
    async ({ action, payableId }) => {
      // v1 flipped status (and, when approving, the client workflow status) in a
      // single PATCH. v3 splits those: `open` moves a draft out of Created, and
      // the approval is its own workflow-status verb.
      let payable = await payablesApi.openPayable({ payableId, ifMatch: "*" });

      if (action === "approve") {
        payable = await payablesApi.updatePayableWorkflowStatus({
          payableId,
          updatePayableWorkflowStatusRequest: {
            payerApprovalStatus:
              UpdatePayableWorkflowStatusRequestPayerApprovalStatus.APPROVED
          }
        });
      }

      return {
        payable
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ payable }) => {
        WSQueryCache.invalidateQueries(QUERY_PAYABLES);
        WSQueryCache.invalidateQueries(
          queriesPayableRows.searchPayableRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries(queriesPayables.getPayable.queryKeyBase);
      }
    }
  );
};

export const useMutationUpdatePayrollSettings = (
  memberId: string,
  config?: WSMutationConfig<IPayrollSettings, WSServiceError>
) => {
  return useWSMutation<
    IPayrollSettings,
    WSServiceError,
    IPayrollSettingsUpdate
  >(
    async data => {
      await paymentsService.service.get();
      return paymentsService.payrollSettings.update(memberId, data);
    },
    {
      dependencies: [QUERY_PAYROLL_SETTINGS],
      ...config
    }
  );
};

export const useMutationDownloadInvoicePdf = () => {
  const downloadPdf = useDownloadPdf();
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    void,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(
    // Every step — the raw request, `blob()` and the object-URL work — is
    // awaited inside the mutation function, so a rejection anywhere in the
    // chain lands in `onError` below instead of escaping unhandled. Without
    // that handler a failing PDF endpoint (PLA-3524 returns 503) made the row
    // action look like it did nothing at all.
    async ({ invoiceId }) => {
      // v3 types the PDF read as `void` — the blob is read off the raw
      // response, the same escape hatch used for other binary downloads
      // (see useMutationDownloadPaystubPdf).
      const response = await invoicesApi.getInvoicePdfRaw({ invoiceId });
      const blob = await response.raw.blob();
      const url = window.URL.createObjectURL(blob);
      downloadPdf(url);
    },
    {
      onError: error => {
        openSnackbar({
          type: "warning",
          message: resolveErrorMessage({
            error,
            context: "DownloadFile"
          })
        });
      }
    }
  );
};

export const useMutationDownloadInvoiceReceiptPdf = () => {
  const downloadPdf = useDownloadPdf();
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    void,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      const response = await invoicesApi.getInvoiceReceiptPdfRaw({ invoiceId });
      const blob = await response.raw.blob();
      const url = window.URL.createObjectURL(blob);
      downloadPdf(url);
    },
    {
      onError: error => {
        openSnackbar({
          type: "warning",
          message: resolveErrorMessage({
            error,
            context: "DownloadFile"
          })
        });
      }
    }
  );
};
